import { CONTACT_FORM } from '@/dictionary/common';
import { useContactForm } from '@/hooks/useContactForm';
import { Button, Modal, Text, Textarea, TextInput } from '@mantine/core';
import { useState, type FormEvent } from 'react';
import classes from './ContactForm.module.css';

const EMPTY_FORM = { name: '', phone: '', email: '', message: '' };

export function ContactForm() {
  const [values, setValues] = useState(EMPTY_FORM);
  const { submit, isSubmitting, errorMessage, isSuccessOpen, closeSuccess } = useContactForm(() =>
    setValues(EMPTY_FORM),
  );
  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    submit(values);
  };
  const updateField = (field: keyof typeof EMPTY_FORM) => (value: string) =>
    setValues((current) => ({ ...current, [field]: value }));

  return (
    <>
      <form className={classes.card} onSubmit={handleSubmit} noValidate>
        <h3 className={classes.title}>{CONTACT_FORM.title}</h3>
        <p className={classes.subtitle}>{CONTACT_FORM.subtitle}</p>

        <div className={classes.fields}>
          <TextInput
            id="contact-name"
            label={CONTACT_FORM.nameLabel}
            placeholder={CONTACT_FORM.namePlaceholder}
            value={values.name}
            onChange={(event) => updateField('name')(event.currentTarget.value)}
            required
            autoComplete="name"
          />
          <TextInput
            id="contact-phone"
            type="tel"
            label={CONTACT_FORM.phoneLabel}
            placeholder={CONTACT_FORM.phonePlaceholder}
            value={values.phone}
            onChange={(event) => updateField('phone')(event.currentTarget.value)}
            required
            autoComplete="tel"
          />
          <TextInput
            id="contact-email"
            type="email"
            label={CONTACT_FORM.emailLabel}
            placeholder={CONTACT_FORM.emailPlaceholder}
            value={values.email}
            onChange={(event) => updateField('email')(event.currentTarget.value)}
            required
            autoComplete="email"
          />
          <Textarea
            id="contact-message"
            label={CONTACT_FORM.messageLabel}
            placeholder={CONTACT_FORM.messagePlaceholder}
            value={values.message}
            onChange={(event) => updateField('message')(event.currentTarget.value)}
          />
        </div>

        <Button type="submit" className={classes.submit} loading={isSubmitting}>
          {isSubmitting ? CONTACT_FORM.submittingLabel : CONTACT_FORM.submitLabel}
        </Button>

        <p className={classes.note}>{CONTACT_FORM.note}</p>

        {errorMessage ? (
          <p className={`${classes.status} ${classes.error}`} role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>

      <Modal
        opened={isSuccessOpen}
        onClose={closeSuccess}
        title={CONTACT_FORM.successTitle}
        closeButtonProps={{ 'aria-label': CONTACT_FORM.successClose }}
      >
        <Text className={classes.successBody}>{CONTACT_FORM.successBody}</Text>
      </Modal>
    </>
  );
}
